import React from 'react';
import { connect } from 'react-redux';

import Slide from '@mui/material/Slide';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';

// @mui/icons-material
import Close from '@mui/icons-material/Close';

import withStyles from '@mui/styles/withStyles';

import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import { constructPhoneRolesString, showAlert } from '../utils/utils';

import DA from '../store/ReduxAccess';
import FormProcessor from '../common/FormProcessor';

import NewOrgWithContactPanel from './NewOrgWithContactPanel';
import NewOrgWithoutContactPanel from './NewOrgWithoutContactPanel';
import AlertModal from './AlertModal';

import CrmButton from './CrmButton';

const localStyles = (theme) => ({
  newReferrerOrgModal: {
    minWidth: '600px',
    [theme.breakpoints.down('md')]: {
      top: 260,
    },
  },
});

/*
 * Call with something like:
  <IcfReferrerFirmModal
    firmType={'Educational Consultant'}
    onSuccess={(visibleFirmType, visibleFirmId, contactId) => this.handleNewFirmSuccess(visibleFirmType, visibleFirmId, contactId)}
    onCancel={() => this.whatever()}
  />
 */

const Transition = (props) => {
  return <Slide direction="down" {...props} />;
};

class IcfReferrerFirmModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      errorMessage: undefined,
      showingFormWithContact: true,
      firmType: this.props.firmType,
      companyName: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
      companyDescription: '',
      companyTemperature: 'Unknown',
      contactName: '',
      contactJobTitle: '',
      contactDescription: '',
      contactPhone1Roles: '',
      contactPhone1Number: '',
      contactPhone2Roles: '',
      contactPhone2Number: '',
      contactEmail: '',
      contactOwnerId: DA.getLoggedInUserPersonId(),
      contactTemperature: 'Unknown',
      webUrl: '',
      shouldShowAlertModal: false,
      alertModalText: '',
    };
  }

  validationPolicyWithContactInfo = {
    requireFirmName: true,
    requireFirmType: true,
    requireAddress: false,
    forbidPartialAddress: true,
    allowZipFreeAddress: true,
    requireContact: true,
    requireJobTitle: false,
    requireContactDescription: false,
    requireContactPhone: false,
    requireContactEmail: false,
    requireAtLeaseOneOfContactPhoneOrEmail: true,
  };

  validationPolicyWithoutContactInfo = {
    requireFirmName: true,
    requireFirmType: true,
    requireAddress: false,
    forbidPartialAddress: true,
    allowZipFreeAddress: true,
    requireContact: false,
    requireJobTitle: false,
    requireContactDescription: false,
    requireContactPhone: false,
    requireContactEmail: false,
    requireAtLeaseOneOfCompanyPhoneOrEmail: true,
  };

  cleanState = {
    errorMessage: undefined,
    firmType: this.props.firmType,
    companyName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
    companyDescription: '',
    contactName: '',
    contactJobTitle: '',
    contactDescription: '',
    contactPhone1Roles: '',
    contactPhone1Number: '',
    contactPhone2Roles: '',
    contactPhone2Number: '',
    contactEmail: '',
    contactOwnerId: DA.getLoggedInUserPersonId(),
  };

  componentDidMount() {
    this.setState({ contactOwnerId: DA.getLoggedInUserPersonId() });
  }

  componentDidUpdate(prevProps) {
    if (this.props.firmType !== prevProps.firmType) {
      this.setState({ firmType: this.props.firmType });
    }
  }

  allPossiblePhoneRoles = ['Mobile', 'Business', 'Home', 'Work', 'Fax'];
  possibleContactPhoneRoles = ['Work', 'Mobile', 'Home'];
  possibleOrgPhoneRoles = ['Business'];

  erase() {
    this.setState({ ...this.cleanState });
  }

  onSuccessfulSubmission(visibleFirmType, visibleFirmId, contactId) {
    this.props.onSuccess(visibleFirmType, visibleFirmId, contactId);
  }

  handleTextChange(event) {
    const rawValue = event.target.value;
    const value = event.target.id === 'contactEmail' ? rawValue.trim() : rawValue;
    this.setState({ [event.target.id]: value });
  }

  handleCompanyStateChange(newValue) {
    this.setState({ state: newValue });
  }

  handleOkClick() {
    this.validateAndSubmit();
  }

  validateAndSubmit() {
    const validation = this.validateForm();
    if (validation.valid) {
      this.submit();
    } else {
      showAlert(this, validation.errorMessage);
    }
  }

  validateForm() {
    const policy = this.state.showingFormWithContact
      ? this.validationPolicyWithContactInfo
      : this.validationPolicyWithoutContactInfo;
    return FormProcessor.validateForm(this.state, policy);
  }

  submit() {
    if (this.state.showingFormWithContact) {
      FormProcessor.submitOrgAndContactForm(this);
    } else {
      FormProcessor.submitOrgWithoutContactForm(this);
    }
  }

  cancelForm() {
    this.props.onCancel({ action: 'canceled' });
  }

  handleContactPhone1RolesChange(rolesObj) {
    const newPhoneRoles = constructPhoneRolesString(rolesObj, this.allPossiblePhoneRoles);
    this.setState({ contactPhone1Roles: newPhoneRoles });
  }

  handleContactPhone1NumberChange(newNumber) {
    this.setState({ contactPhone1Number: newNumber });
  }

  handleContactPhone2RolesChange(rolesObj) {
    const newPhoneRoles = constructPhoneRolesString(rolesObj, this.allPossiblePhoneRoles);
    this.setState({ contactPhone2Roles: newPhoneRoles });
  }

  handleContactPhone2NumberChange(newNumber) {
    this.setState({ contactPhone2Number: newNumber });
  }

  handleBusinessTypeChange(newValue) {
    this.setState({ firmType: newValue });
  }

  computeOrgTypeLabel(firmType) {
    if (firmType == 'Educational Consultant') {
      return 'Consultancy';
    } else {
      if (firmType) {
        return firmType;
      } else {
        return 'Organization';
      }
    }
  }

  computeModalTitle() {
    return 'Register ' + this.computeOrgTypeLabel(this.state.firmType);
  }

  cancelUnlessBackdropClick(event, reason, onCancel) {
    if (reason !== 'backdropClick') {
      onCancel(event, reason);
    }
  }

  computeOtherButtonLabel(showingFormWithContact) {
    if (showingFormWithContact) {
      return 'Switch to no-contact form';
    } else {
      return 'Switch to form with contact info';
    }
  }

  render() {
    const { classes, onCancel } = this.props;
    const { shouldShowAlertModal, alertModalText } = this.state;
    const title = this.computeModalTitle();
    const otherButtonLabel = this.computeOtherButtonLabel(this.state.showingFormWithContact);
    return (
      <div>
        <AlertModal
          show={shouldShowAlertModal}
          title="Alert"
          text={alertModalText}
          closeFunc={() => this.setState({ shouldShowAlertModal: false })}
        />

        <Dialog
          classes={{
            root: /* classes.center + " " +*/ classes.modalRoot,
            paper: classes.modal,
          }}
          open={!!open}
          maxWidth="md"
          TransitionComponent={Transition}
          disableEscapeKeyDown={true}
          onClose={(event, reason) => cancelUnlessBackdropClick(event, reason, onCancel)}
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
        >
          <DialogTitle id="modal-title" className={classes.modalHeader}>
            <IconButton className={classes.modalCloseButton} onClick={onCancel} size="large">
              <Close className={classes.modalClose} />
            </IconButton>
            {title}
          </DialogTitle>
          <DialogContent id="modal-description" className={classes.modalBody}>
            {this.state.showingFormWithContact ? (
              <NewOrgWithContactPanel
                ownerModal={this}
                firmType={this.state.firmType}
                onFirmTypeChange={(newType) => this.setState({ firmType: newType })}
              />
            ) : (
              <NewOrgWithoutContactPanel
                ownerModal={this}
                firmType={this.state.firmType}
                onFirmTypeChange={(newType) => this.setState({ firmType: newType })}
              />
            )}
          </DialogContent>
          <DialogActions className={classes.modalFooter}>
            <CrmButton
              outlined
              onClick={() => this.setState({ showingFormWithContact: !this.state.showingFormWithContact })}
            >
              {otherButtonLabel}
            </CrmButton>
            <div style={{ width: '60px' }} />
            <CrmButton outlined onClick={onCancel}>
              {'Cancel'}
            </CrmButton>
            <CrmButton filled onClick={() => this.handleOkClick()}>
              {'Register'}
            </CrmButton>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({});

const actionProducers = {
  setApiResponseInfo,
};

export default connect(mapStateToProps, actionProducers)(withStyles(localStyles)(IcfReferrerFirmModal));
